<template>
  <div class="phone-input-container">
    <div class="country-selector" @click="toggleDropdown">
      <div class="selected-country">
        <img :src="getFlagUrl(selectedCountry.code)" :alt="selectedCountry.name" class="country-flag">
        <span class="country-code">{{ selectedCountry.dialCode }}</span>
      </div>
      <div class="dropdown-arrow">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </div>
    </div>
    
    <input
      type="tel"
      :value="phoneNumber"
      @input="handlePhoneInput"
      class="phone-number-input"
      :placeholder="placeholder"
    />
    
    <div v-if="showDropdown" class="countries-dropdown">
      <div class="search-box">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Rechercher un pays..."
          @input="filterCountries"
        />
      </div>
      <div class="countries-list">
        <div
          v-for="country in filteredCountries"
          :key="country.code"
          class="country-option"
          @click="selectCountry(country)"
        >
          <img :src="getFlagUrl(country.code)" :alt="country.name" class="country-flag">
          <span class="country-name">{{ country.name }}</span>
          <span class="country-code">{{ country.dialCode }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Numéro de téléphone'
  }
});

const emit = defineEmits(['update:modelValue', 'country-change']);

const countries = [
  { code: 'SN', name: 'Sénégal', dialCode: '221' },
  { code: 'FR', name: 'France', dialCode: '33' },
  { code: 'US', name: 'États-Unis', dialCode: '1' },
  { code: 'CI', name: 'Côte d\'Ivoire', dialCode: '225' },
  { code: 'ML', name: 'Mali', dialCode: '223' },
  { code: 'GN', name: 'Guinée', dialCode: '224' },
  { code: 'BF', name: 'Burkina Faso', dialCode: '226' },
  { code: 'NE', name: 'Niger', dialCode: '227' },
  { code: 'TG', name: 'Togo', dialCode: '228' },
  { code: 'BJ', name: 'Bénin', dialCode: '229' },
  { code: 'MR', name: 'Mauritanie', dialCode: '222' },
  { code: 'GM', name: 'Gambie', dialCode: '220' },
  { code: 'GW', name: 'Guinée-Bissau', dialCode: '245' },
  { code: 'GH', name: 'Ghana', dialCode: '233' },
  { code: 'NG', name: 'Nigeria', dialCode: '234' },
  { code: 'CM', name: 'Cameroun', dialCode: '237' },
  { code: 'GA', name: 'Gabon', dialCode: '241' },
  { code: 'CG', name: 'Congo', dialCode: '242' },
  { code: 'CD', name: 'RDC', dialCode: '243' },
  { code: 'RW', name: 'Rwanda', dialCode: '250' },
  { code: 'BI', name: 'Burundi', dialCode: '257' },
  { code: 'TZ', name: 'Tanzanie', dialCode: '255' },
  { code: 'KE', name: 'Kenya', dialCode: '254' },
  { code: 'UG', name: 'Ouganda', dialCode: '256' },
  { code: 'ET', name: 'Éthiopie', dialCode: '251' },
  { code: 'SD', name: 'Soudan', dialCode: '249' },
  { code: 'SS', name: 'Soudan du Sud', dialCode: '211' },
  { code: 'ER', name: 'Érythrée', dialCode: '291' },
  { code: 'DJ', name: 'Djibouti', dialCode: '253' },
  { code: 'SO', name: 'Somalie', dialCode: '252' },
  { code: 'LY', name: 'Libye', dialCode: '218' },
  { code: 'TN', name: 'Tunisie', dialCode: '216' },
  { code: 'DZ', name: 'Algérie', dialCode: '213' },
  { code: 'MA', name: 'Maroc', dialCode: '212' },
  { code: 'EG', name: 'Égypte', dialCode: '20' },
  { code: 'ZA', name: 'Afrique du Sud', dialCode: '27' },
  { code: 'NA', name: 'Namibie', dialCode: '264' },
  { code: 'BW', name: 'Botswana', dialCode: '267' },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '263' },
  { code: 'MZ', name: 'Mozambique', dialCode: '258' },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '263' },
  { code: 'ZM', name: 'Zambie', dialCode: '260' },
  { code: 'AO', name: 'Angola', dialCode: '244' },
  { code: 'MG', name: 'Madagascar', dialCode: '261' },
  { code: 'MU', name: 'Maurice', dialCode: '230' },
  { code: 'SC', name: 'Seychelles', dialCode: '248' },
  { code: 'KM', name: 'Comores', dialCode: '269' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '44' },
  { code: 'DE', name: 'Allemagne', dialCode: '49' },
  { code: 'IT', name: 'Italie', dialCode: '39' },
  { code: 'ES', name: 'Espagne', dialCode: '34' },
  { code: 'PT', name: 'Portugal', dialCode: '351' },
  { code: 'BE', name: 'Belgique', dialCode: '32' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '31' },
  { code: 'CH', name: 'Suisse', dialCode: '41' },
  { code: 'AT', name: 'Autriche', dialCode: '43' },
  { code: 'SE', name: 'Suède', dialCode: '46' },
  { code: 'NO', name: 'Norvège', dialCode: '47' },
  { code: 'DK', name: 'Danemark', dialCode: '45' },
  { code: 'FI', name: 'Finlande', dialCode: '358' },
  { code: 'PL', name: 'Pologne', dialCode: '48' },
  { code: 'CZ', name: 'République tchèque', dialCode: '420' },
  { code: 'SK', name: 'Slovaquie', dialCode: '421' },
  { code: 'HU', name: 'Hongrie', dialCode: '36' },
  { code: 'RO', name: 'Roumanie', dialCode: '40' },
  { code: 'BG', name: 'Bulgarie', dialCode: '359' },
  { code: 'GR', name: 'Grèce', dialCode: '30' },
  { code: 'TR', name: 'Turquie', dialCode: '90' },
  { code: 'RU', name: 'Russie', dialCode: '7' },
  { code: 'UA', name: 'Ukraine', dialCode: '380' },
  { code: 'BY', name: 'Biélorussie', dialCode: '375' },
  { code: 'MD', name: 'Moldova', dialCode: '373' },
  { code: 'LT', name: 'Lituanie', dialCode: '370' },
  { code: 'LV', name: 'Lettonie', dialCode: '371' },
  { code: 'EE', name: 'Estonie', dialCode: '372' },
  { code: 'IS', name: 'Islande', dialCode: '354' },
  { code: 'IE', name: 'Irlande', dialCode: '353' },
  { code: 'CA', name: 'Canada', dialCode: '1' },
  { code: 'MX', name: 'Mexique', dialCode: '52' },
  { code: 'BR', name: 'Brésil', dialCode: '55' },
  { code: 'AR', name: 'Argentine', dialCode: '54' },
  { code: 'CL', name: 'Chili', dialCode: '56' },
  { code: 'CO', name: 'Colombie', dialCode: '57' },
  { code: 'PE', name: 'Pérou', dialCode: '51' },
  { code: 'VE', name: 'Venezuela', dialCode: '58' },
  { code: 'EC', name: 'Équateur', dialCode: '593' },
  { code: 'BO', name: 'Bolivie', dialCode: '591' },
  { code: 'PY', name: 'Paraguay', dialCode: '595' },
  { code: 'UY', name: 'Uruguay', dialCode: '598' },
  { code: 'AU', name: 'Australie', dialCode: '61' },
  { code: 'NZ', name: 'Nouvelle-Zélande', dialCode: '64' },
  { code: 'JP', name: 'Japon', dialCode: '81' },
  { code: 'KR', name: 'Corée du Sud', dialCode: '82' },
  { code: 'CN', name: 'Chine', dialCode: '86' },
  { code: 'IN', name: 'Inde', dialCode: '91' },
  { code: 'ID', name: 'Indonésie', dialCode: '62' },
  { code: 'MY', name: 'Malaisie', dialCode: '60' },
  { code: 'SG', name: 'Singapour', dialCode: '65' },
  { code: 'TH', name: 'Thaïlande', dialCode: '66' },
  { code: 'VN', name: 'Vietnam', dialCode: '84' },
  { code: 'PH', name: 'Philippines', dialCode: '63' }
];

const showDropdown = ref(false);
const searchQuery = ref('');
const selectedCountry = ref(countries[0]); // Sénégal par défaut
const phoneNumber = ref('');

const filteredCountries = computed(() => {
  if (!searchQuery.value) return countries;
  const query = searchQuery.value.toLowerCase();
  return countries.filter(country => 
    country.name.toLowerCase().includes(query) ||
    country.dialCode.includes(query)
  );
});

const getFlagUrl = (countryCode) => {
  return `https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`;
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const selectCountry = (country) => {
  selectedCountry.value = country;
  showDropdown.value = false;
  emit('country-change', country);
  updatePhoneNumber();
};

const handlePhoneInput = (event) => {
  const value = event.target.value.replace(/\D/g, '');
  phoneNumber.value = value;
  updatePhoneNumber();
};

const updatePhoneNumber = () => {
  const fullNumber = selectedCountry.value.dialCode + phoneNumber.value;
  emit('update:modelValue', fullNumber);
};

const detectCountryFromNumber = (number) => {
  if (!number) return;
  
  // Trouver le pays correspondant à l'indicatif
  const country = countries.find(c => number.startsWith(c.dialCode));
  if (country) {
    selectedCountry.value = country;
    phoneNumber.value = number.slice(country.dialCode.length);
  }
};

// Observer les changements de la valeur du modèle
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    detectCountryFromNumber(newValue);
  }
});

// Fermer le dropdown quand on clique en dehors
const handleClickOutside = (event) => {
  if (!event.target.closest('.phone-input-container')) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  if (props.modelValue) {
    detectCountryFromNumber(props.modelValue);
  }
});
</script>

<style scoped>
.phone-input-container {
  position: relative;
  display: flex;
  width: 100%;
}

.country-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  cursor: pointer;
  min-width: 100px;
  margin-right: 8px;
}

.selected-country {
  display: flex;
  align-items: center;
  gap: 8px;
}

.country-flag {
  width: 20px;
  height: 15px;
  object-fit: cover;
}

.country-code {
  font-size: 14px;
  color: #1A1D1F;
}

.dropdown-arrow {
  color: #6F767E;
}

.phone-number-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  font-size: 14px;
  color: #1A1D1F;
  background: white;
}

.countries-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 300px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.search-box {
  padding: 8px;
  border-bottom: 1px solid #E8ECEF;
}

.search-box input {
  width: 100%;
  padding: 8px;
  border: 1px solid #E8ECEF;
  border-radius: 4px;
  font-size: 14px;
}

.countries-list {
  max-height: 300px;
  overflow-y: auto;
}

.country-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.country-option:hover {
  background: #F9FAFB;
}

.country-name {
  flex: 1;
  font-size: 14px;
  color: #1A1D1F;
}
</style> 